import React, { useEffect, useRef, useState } from 'react';
import { introData, navigationLinks } from '../data/mock';

const IntroSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: 'PHOTOS', id: 'photos' },
    { label: 'ABOUT', id: 'about' },
    { label: 'SERVICES', id: 'services' },
    { label: 'CONTACT', id: 'contact' }
  ];

  return (
    <section
      id="intro"
      ref={sectionRef}
      className="relative min-h-screen bg-black py-24 overflow-hidden"
    >
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <span className="text-pink-400 text-xs tracking-[0.3em] block mb-4">INTRO</span>
        <h2 className={`text-white text-3xl md:text-4xl lg:text-5xl font-extralight tracking-wide transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {introData.subtitle}
        </h2>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}>
            <div className="relative overflow-hidden">
              <img
                src={introData.image}
                alt="Sofia Russo"
                className="w-full h-auto max-h-[700px] object-contain transition-all duration-700"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
            {/* Decorative border */}
            <div className="absolute -bottom-4 -right-4 w-full h-full border border-pink-400/30 -z-10" />
          </div>

          {/* Text content */}
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
            <h3 className="text-pink-400 text-sm tracking-[0.3em] mb-6">
              {introData.title}
            </h3>
            
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              {introData.description}
            </p>

            <blockquote className="border-l-2 border-pink-400 pl-6 mb-12">
              <p className="text-white/50 italic text-lg">
                "{introData.quote}"
              </p>
            </blockquote>

            {/* Quick links */}
            <div>
              <h4 className="text-white text-sm tracking-[0.2em] mb-6">WHERE WOULD YOU LIKE TO START?</h4>
              <div className="flex flex-wrap gap-4">
                {quickLinks.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="px-6 py-3 border border-white/20 text-white/70 text-xs tracking-[0.2em] hover:border-pink-400 hover:text-pink-400 transition-all duration-300"
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </section>
  );
};

export default IntroSection;
